import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import SearchableSelect from '../SearchableSelect';
import { SELECT_OPTIONS, FIELD_RULES } from './adminHelpers';
import AdminImageUploadInput from './AdminImageUploadInput';

// Field keys that should render an inline image uploader instead of a text
// input. Add new entries here whenever a CRUD resource needs an image picker.
const IMAGE_FIELDS = ['image', 'image_url', 'logo_url'];

// Maps `resource` name to the upload-prefix the backend uses for filename
// namespacing. Falls back to the resource name (singular-ish) when unknown.
const IMAGE_PREFIX_BY_RESOURCE = {
  categories: 'category',
  main_categories: 'maincategory',
  sub_categories: 'subcategory',
  suppliers: 'logo',
};

const AdminRecordForm = ({ fields, initial, onSave, onCancel, resource, parentOptions = {} }) => {
  // Fields that store arrays in the DB but should render as a multi-line textarea
  // (one item per line). Convert in both directions at save time.
  const ARRAY_TEXTAREA_FIELDS = ['features'];

  const [values, setValues] = useState(() => {
    const obj = {};
    fields.forEach((f) => {
      let v = initial?.[f];
      if (ARRAY_TEXTAREA_FIELDS.includes(f)) {
        // array → newline-joined string
        v = Array.isArray(v) ? v.join('\n') : (v || '');
      } else if (v === undefined || v === null || v === '') {
        const opts = SELECT_OPTIONS[f];
        v = opts ? opts[0].value : '';
      }
      obj[f] = v;
    });
    if (resource === 'suppliers') {
      obj.categories = (initial?.categories || []).join(', ');
    }
    return obj;
  });

  const isLongField = (f) => ['description', ...ARRAY_TEXTAREA_FIELDS].includes(f);
  const isImageField = (f) => IMAGE_FIELDS.includes(f);
  const ruleFor = (f) => FIELD_RULES[`${resource}.${f}`] || {};
  const parentOpts = (f) => parentOptions[f];
  const imagePrefix = IMAGE_PREFIX_BY_RESOURCE[resource] || (resource || 'image').replace(/_/g, '-');

  const buildPayload = () => {
    const out = { ...values };
    ARRAY_TEXTAREA_FIELDS.forEach((f) => {
      if (out[f] !== undefined) {
        out[f] = String(out[f] || '')
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);
      }
    });
    return out;
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(buildPayload()); }} className="grid grid-cols-2 gap-3">
      {fields.map((f) => {
        const rule = ruleFor(f);
        const isRequired = !!rule.required || ['main_category_id', 'category_id'].includes(f);
        const opts = parentOpts(f);
        return (
          <div key={f} className={isLongField(f) || isImageField(f) ? 'col-span-2' : ''}>
            <Label className="text-xs capitalize">
              {f.replace(/_/g, ' ')}{isRequired && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {isImageField(f) ? (
              <AdminImageUploadInput
                value={values[f]}
                onChange={(url) => setValues({ ...values, [f]: url })}
                prefix={imagePrefix}
                testId={`field-${f}`}
              />
            ) : opts ? (
              <SearchableSelect
                options={opts.map((o) => ({ id: o.id, name: o.name, slug: o.slug, hint: o.slug || '' }))}
                value={values[f]}
                onChange={(id) => setValues({ ...values, [f]: id })}
                required={isRequired}
                placeholder="— Select —"
                testId={`field-${f}`}
              />
            ) : SELECT_OPTIONS[f] ? (
              <select
                value={String(values[f])}
                onChange={(e) => {
                  const raw = e.target.value;
                  const opt = SELECT_OPTIONS[f].find((o) => String(o.value) === raw);
                  setValues({ ...values, [f]: opt ? opt.value : raw });
                }}
                className="w-full h-10 text-sm border border-gray-300 rounded px-2 focus:outline-none focus:border-[#ee5e23]"
                data-testid={`field-${f}`}
              >
                {SELECT_OPTIONS[f].map((o) => (
                  <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
                ))}
              </select>
            ) : isLongField(f) ? (
              <Textarea
                value={values[f]}
                onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                rows={f === 'features' ? 6 : 3}
                placeholder={rule.placeholder}
                required={isRequired}
              />
            ) : (
              <Input
                type={rule.type || (['price','order','rating','established'].includes(f) ? 'number' : 'text')}
                value={values[f]}
                onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                required={isRequired}
                pattern={rule.pattern}
                maxLength={rule.maxLength}
                inputMode={rule.inputMode}
                placeholder={rule.placeholder}
                title={rule.title}
                data-testid={`field-${f}`}
              />
            )}
          </div>
        );
      })}
      {resource === 'suppliers' && (
        <div className="col-span-2">
          <Label className="text-xs">Categories (comma separated)</Label>
          <Input
            value={values.categories}
            onChange={(e) => setValues({ ...values, categories: e.target.value })}
            placeholder="Steel Pipes, TMT Bars"
          />
        </div>
      )}
      <div className="col-span-2 flex gap-2 justify-end mt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-[#ee5e23] hover:bg-[#d24e15] text-white">Save</Button>
      </div>
    </form>
  );
};

export default AdminRecordForm;
